import { createContext, useContext, useState } from "react";
import Toast from "../components/ui/Toast";

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = (messageOrConfig, variant = "success", duration = 4000) => {
    const id = Date.now();

    // Support both object config and separate parameters
    let message, finalVariant, finalDuration;

    if (typeof messageOrConfig === "object" && messageOrConfig !== null) {
      // Object config format: { message, type/variant, duration }
      message = messageOrConfig.message;
      finalVariant = messageOrConfig.type || messageOrConfig.variant || variant;
      finalDuration = messageOrConfig.duration || duration;
    } else {
      // Separate parameters format
      message = messageOrConfig;
      finalVariant = variant;
      finalDuration = duration;
    }

    const newToast = {
      id,
      message,
      variant: finalVariant,
      duration: finalDuration,
    };

    setToasts((prev) => [...prev, newToast]);

    // Auto-remove toast after duration
    if (finalDuration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, finalDuration);
    }
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const showSuccess = (message, duration) =>
    showToast(message, "success", duration);
  const showError = (message, duration) =>
    showToast(message, "error", duration);
  const showWarning = (message, duration) =>
    showToast(message, "warning", duration);
  const showInfo = (message, duration) => showToast(message, "info", duration);

  return (
    <ToastContext.Provider
      value={{
        showToast,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        removeToast,
      }}
    >
      {children}

      {/* Render all active toasts */}
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          open={true}
          message={toast.message}
          variant={toast.variant}
          duration={0} // Let context handle timing
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </ToastContext.Provider>
  );
};
